import { useEffect, useState } from 'react';
import { libraryStates } from '../../utils/library-states';
import {
	API_BASE,
	COLUMN_SIZING_STORAGE_KEY,
	STICKY_COLUMNS_STORAGE_KEY,
} from './constants';

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
	year: 'numeric',
	month: 'short',
	day: '2-digit',
});

const LOCAL_DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
	month: 'short',
	day: '2-digit',
	year: 'numeric',
	hour: 'numeric',
	minute: '2-digit',
});

const DISPLAY_DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
	year: 'numeric',
	month: 'short',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hour12: false,
});

const USD_CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	maximumFractionDigits: 2,
});

export { classNames } from '../../utils/classNames';

export function getStoredStickyColumns() {
	try {
		const raw = localStorage.getItem(STICKY_COLUMNS_STORAGE_KEY);
		if (!raw) return { name: true };
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : { name: true };
	} catch {
		return { name: true };
	}
}

export function getStoredColumnSizing() {
	try {
		const raw = localStorage.getItem(COLUMN_SIZING_STORAGE_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch {
		return {};
	}
}

export function parseDateValue(value) {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '—';
	return DATE_FORMATTER.format(date);
}

export function parseServerDate(value) {
	if (!value) return null;

	const raw = String(value).trim();

	if (
		raw.endsWith('Z') ||
		/[+-]\d{2}:\d{2}$/.test(raw) ||
		/[+-]\d{4}$/.test(raw)
	) {
		const date = new Date(raw);
		return Number.isNaN(date.getTime()) ? null : date;
	}

	const normalized = raw.replace(' ', 'T') + 'Z';
	const date = new Date(normalized);
	return Number.isNaN(date.getTime()) ? null : date;
}

export function timeAgo(value) {
	const date = parseServerDate(value);
	if (!date) return '—';

	const diffMs = Date.now() - date.getTime();

	if (diffMs < 0 && Math.abs(diffMs) < 5 * 60 * 1000) {
		return 'just now';
	}

	if (diffMs < 0) {
		const futureSec = Math.floor(Math.abs(diffMs) / 1000);
		const futureMin = Math.floor(futureSec / 60);
		const futureHr = Math.floor(futureMin / 60);
		const futureDay = Math.floor(futureHr / 24);

		if (futureSec < 60) return 'just now';
		if (futureMin < 60) return `in ${futureMin}m`;
		if (futureHr < 24) return `in ${futureHr}h`;
		return `in ${futureDay}d`;
	}

	const sec = Math.floor(diffMs / 1000);
	const min = Math.floor(sec / 60);
	const hr = Math.floor(min / 60);
	const day = Math.floor(hr / 24);

	if (sec < 60) return `${sec}s ago`;
	if (min < 60) return `${min}m ago`;
	if (hr < 24) return `${hr}h ago`;
	return `${day}d ago`;
}

export function normalizeDomain(domain) {
	if (!domain) return '—';

	try {
		const url = new URL(domain);
		return url.hostname;
	} catch {
		return String(domain).replace(/^https?:\/\//, '').replace(/\/$/, '');
	}
}

export function sanitizeExternalUrl(raw) {
	if (!raw) return '';

	try {
		const url = new URL(String(raw));
		if (url.protocol === 'http:' || url.protocol === 'https:') {
			return url.toString();
		}
		return '';
	} catch {
		return '';
	}
}

export function statusStyleByValue(value) {
	switch (String(value)) {
		case '1':
			return {
				wrap: 'bg-emerald-50 ring-emerald-600/15 text-emerald-800',
				dot: 'bg-emerald-500',
			};
		case '2':
			return {
				wrap: 'bg-violet-50 ring-violet-600/15 text-violet-800',
				dot: 'bg-violet-500',
			};
		case '3':
			return {
				wrap: 'bg-sky-50 ring-sky-600/15 text-sky-800',
				dot: 'bg-sky-500',
			};
		case '4':
			return {
				wrap: 'bg-amber-50 ring-amber-600/15 text-amber-900',
				dot: 'bg-amber-500',
			};
		case '5':
			return {
				wrap: 'bg-zinc-100 ring-zinc-600/15 text-zinc-800',
				dot: 'bg-zinc-500',
			};
		case '6':
			return {
				wrap: 'bg-rose-50 ring-rose-600/15 text-rose-800',
				dot: 'bg-rose-500',
			};
		default:
			return {
				wrap: 'bg-zinc-50 ring-zinc-600/10 text-zinc-700',
				dot: 'bg-zinc-400',
			};
	}
}

export async function fetchSalesRepresentatives({ token }) {
	const res = await fetch(`${API_BASE}/sales-representatives`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
	});

	if (!res.ok) {
		const payload = await res.json().catch(() => ({}));
		throw new Error(
			payload?.detail || `Failed to load sales representatives (${res.status})`,
		);
	}

	return res.json();
}

export async function fetchInstitutionLogs({
	instituteId,
	page = 1,
	pageSize = 25,
	sortDir = 'desc',
	token,
}) {
	const params = new URLSearchParams();
	params.set('page', String(page));
	params.set('page_size', String(pageSize));
	params.set('sort_dir', sortDir);

	const res = await fetch(
		`${API_BASE}/activity-log/institute/${instituteId}?${params.toString()}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		},
	);

	if (!res.ok) {
		const payload = await res.json().catch(() => ({}));
		throw new Error(payload?.detail || `Failed to load logs (${res.status})`);
	}

	return res.json();
}

export async function fetchInstitutionPayments({
	institutionId,
	page = 1,
	pageSize = 25,
	sortBy = 'date_payment',
	sortDir = 'desc',
	token,
}) {
	const params = new URLSearchParams();
	params.set('page', String(page));
	params.set('page_size', String(pageSize));
	params.set('sort_by', sortBy);
	params.set('sort_dir', sortDir);

	const res = await fetch(
		`${API_BASE}/institutions/${institutionId}/payments?${params.toString()}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		},
	);

	if (!res.ok) {
		const payload = await res.json().catch(() => ({}));
		throw new Error(
			payload?.detail || `Failed to load payments (${res.status})`,
		);
	}

	return res.json();
}

export function formatLocalDateTime(value) {
	const date = parseServerDate(value);
	if (!date) return '—';
	return LOCAL_DATE_TIME_FORMATTER.format(date);
}

export function getLogBadgeStyle(field) {
	switch (field) {
		case 'state_id':
			return 'bg-amber-50 text-amber-700 ring-amber-600/10';
		case 'Date_demo':
			return 'bg-violet-50 text-violet-700 ring-violet-600/10';
		case 'Sales_Representative':
			return 'bg-sky-50 text-sky-700 ring-sky-600/10';
		case 'Contact_institution':
			return 'bg-emerald-50 text-emerald-700 ring-emerald-600/10';
		default:
			return 'bg-zinc-50 text-zinc-700 ring-zinc-600/10';
	}
}

export function formatLogValue(field, value, salesRepOptions = []) {
	if (value == null || value === '') return '—';

	if (field === 'state_id') {
		const found = libraryStates.find((state) => String(state.value) === String(value));
		return found?.label || String(value);
	}

	if (field === 'Sales_Representative') {
		const found = salesRepOptions.find(
			(salesRep) => String(salesRep.value) === String(value),
		);
		return found?.label || String(value);
	}

	if (field === 'Date_demo') {
		return formatLocalDateTime(value);
	}

	return String(value);
}

export function getStateValueFromRow(row) {
	if (row?.state_id != null) return String(row.state_id);
	if (row?.state?.id != null) return String(row.state.id);

	const label = (row?.state?.name || '').toLowerCase();
	const found = libraryStates.find((state) => state.label.toLowerCase() === label);
	return found ? String(found.value) : '';
}

export function getFieldLabel(field) {
	switch (field) {
		case 'state_id':
			return 'STATE';
		case 'Date_demo':
			return 'DATE_DEMO';
		case 'Sales_Representative':
			return 'SALES REP';
		case 'Contact_institution':
			return 'CONTACT CLIENT';
		default:
			return field;
	}
}

export function getDisplayValue(field, value, salesRepOptions = []) {
	if (field === 'state_id') {
		const found = libraryStates.find((state) => String(state.value) === String(value));
		return found?.label || '—';
	}

	if (field === 'Sales_Representative') {
		const found = salesRepOptions.find(
			(salesRep) => String(salesRep.value) === String(value),
		);
		return found?.label || value || '—';
	}

	if (field === 'Date_demo') {
		if (!value) return '—';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return '—';
		return DISPLAY_DATE_TIME_FORMATTER.format(date);
	}

	return value || '—';
}

export function useDebouncedValue(value, delay = 350) {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);

	return debounced;
}

export async function patchInstitutionField({ id, payload, token }) {
	const res = await fetch(`${API_BASE}/institutions/${id}/basic`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		body: JSON.stringify(payload),
	});

	if (!res.ok) {
		const payloadError = await res.json().catch(() => ({}));
		throw new Error(
			payloadError?.detail || `Failed to update institution (${res.status})`,
		);
	}

	return res.json();
}

export function formatDateForInput(value) {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	return date.toISOString().slice(0, 10);
}

export function formatCurrency(value) {
	if (value == null) return '—';
	return USD_CURRENCY_FORMATTER.format(Number(value));
}

export function getPaymentStateStyle(name) {
	const key = String(name || '').toLowerCase();

	if (key.includes('completed')) return 'text-emerald-700';
	if (key.includes('pending')) return 'text-amber-700';
	if (key.includes('refund')) return 'text-rose-700';

	return 'text-zinc-700';
}

export function getStoredUserEmail() {
	return (
		localStorage.getItem('email') ||
		localStorage.getItem('userEmail') ||
		localStorage.getItem('username') ||
		''
	);
}

export function formatDateForBackend(dateValue) {
	if (!dateValue) return null;

	const nextDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
	if (Number.isNaN(nextDate.getTime())) return null;

	const year = nextDate.getFullYear();
	const month = String(nextDate.getMonth() + 1).padStart(2, '0');
	const day = String(nextDate.getDate()).padStart(2, '0');

	return `${year}-${month}-${day} 00:00:00`;
}

export function getStickyLeft(table, columnId, stickyColumns) {
	if (!stickyColumns?.[columnId]) return undefined;

	const visibleLeafColumns = table.getVisibleLeafColumns();
	let left = 0;

	for (const col of visibleLeafColumns) {
		if (col.id === columnId) break;
		if (stickyColumns?.[col.id]) {
			left += col.getSize();
		}
	}

	return left;
}

export function isPastDate(value) {
	const date = parseServerDate(value);
	if (!date) return false;

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const compare = new Date(date);
	compare.setHours(0, 0, 0, 0);

	return compare < today;
}

export function exportCsv(rows) {
	const headers = [
		'id',
		'code',
		'name',
		'language',
		'domain',
		'ebooks',
		'category',
		'state_id',
		'state_name',
		'Contact_institution',
		'Sales_Representative',
		'Date_demo',
		'created_at',
		'update_at',
	];

	const escape = (value) => {
		if (value == null) return '';
		const stringValue = Array.isArray(value) ? value.join('; ') : String(value);
		return `"${stringValue.replace(/"/g, '""')}"`;
	};

	const lines = [
		headers.join(','),
		...(rows || []).map((row) =>
			[
				row.id,
				row.code,
				row.name,
				row.language,
				row.domain,
				row.ebooks,
				row.category,
				row.state_id,
				row.state?.name,
				row.Contact_institution,
				row.Sales_Representative,
				row.Date_demo,
				row.created_at,
				row.update_at,
			]
				.map(escape)
				.join(','),
		),
	];

	const blob = new Blob([lines.join('\n')], {
		type: 'text/csv;charset=utf-8;',
	});
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = 'institutions_page.csv';
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}
